import cx from 'classnames'
import { FC, ReactNode, useCallback } from 'react'
import ButtonWithFeedback from '../ButtonWithFeedback/ButtonWithFeedback'
import styles from './ComponentFooter.module.scss'

interface FooterButtonsProps {
  onCancelClick: () => void
  onApplyClick?: () => void
  cancelId?: string
  applyId?: string
  submitText?: string | ReactNode
  cancelText?: string | ReactNode
  errorText?: string
  disableSubmit?: boolean
  disableCancel?: boolean
  /** When defined, controls SUBMITTING state externally and fires onApplyClick immediately. */
  submitting?: boolean
}

const ComponentFooter: FC<FooterButtonsProps> = ({
  onCancelClick,
  onApplyClick,
  cancelId,
  applyId,
  errorText,
  submitText = 'Save',
  cancelText = 'Cancel',
  disableSubmit = false,
  disableCancel = false,
  submitting
}) => {
  const stateControlled = submitting !== undefined
  const showSavingTime = 500

  /* Timer-driven: show SUBMITTING for 500ms, then fire onApplyClick. */
  const showSaving = useCallback(() => {
    if (!onApplyClick) return
    const timer = setTimeout(onApplyClick, showSavingTime)
    return () => clearTimeout(timer)
  }, [onApplyClick])

  /* State-driven: fire onApplyClick immediately; external prop controls the button text. */
  const handleApplyClick = useCallback(() => {
    if (onApplyClick) {
      onApplyClick()
    }
  }, [onApplyClick])

  /* No useMemo needed — props are destructured primitives, so the object literal is cheap. */
  const feedbackProps = stateControlled
    ? { feedbackActive: submitting }
    : { timerToReturnToFirstState: showSavingTime }

  return (
    <>
      {errorText && (
        <div className={styles.errorTextWrapper}>
          <label className={styles.errorText}>{errorText}</label>
        </div>
      )}
      <div className={styles.footerButtons}>
        <button
          id={cancelId}
          data-testid={cancelId}
          className={styles.cancel}
          onClick={onCancelClick}
          disabled={disableCancel}
          type="button"
        >
          {cancelText}
        </button>
        <ButtonWithFeedback
          id={applyId}
          data-testid={applyId}
          className={cx(`${styles.save} confirm`)}
          onClick={stateControlled ? handleApplyClick : showSaving}
          contentUpdate="SUBMITTING"
          {...feedbackProps}
          disabled={disableSubmit}
          type="submit"
        >
          {submitText}
        </ButtonWithFeedback>
      </div>
    </>
  )
}

export default ComponentFooter
